import type { AxiosError } from 'axios';

/**
 * Trækker den fejlbesked SERVEREN sendte ud af en fejl.
 *
 * Baggrund: alle API-fejl kommer fra axios som en `AxiosError`, hvis egen
 * `.message` er standardteksten "Request failed with status code 409". Serveren
 * lægger den rigtige, brugbare besked i svarets `error`-felt, men den kastes væk
 * hvis man bare viser `error.message` - og det gjorde over halvdelen af
 * komponenterne. Resultatet var at fx "CVR 46049985 er allerede registreret på
 * JABJ Transport ApS" blev vist som "Request failed with status code 409".
 *
 * Bruges af response-interceptoren i api.ts, som skriver resultatet tilbage til
 * `error.message`. Dermed viser også de komponenter der (naivt) læser
 * `error.message` den rigtige tekst, uden at de skal ændres enkeltvis.
 */

interface ZodIssue {
  path?: Array<string | number>;
  message?: string;
  code?: string;
  validation?: string;
  received?: string;
  expected?: string;
  minimum?: number;
  maximum?: number;
}

interface ServerErrorBody {
  error?: string;
  message?: string;
  details?: ZodIssue[];
}

/** Fallback-tekster for statuskoder hvor serveren ikke sendte en besked. */
const STATUS_FALLBACKS: Record<number, string> = {
  400: 'Ugyldig forespørgsel.',
  401: 'Din session er udløbet. Log ind igen.',
  403: 'Du har ikke adgang til dette.',
  404: 'Blev ikke fundet.',
  409: 'Konflikt: findes allerede.',
  413: 'Filen er for stor.',
  429: 'For mange forespørgsler - vent et øjeblik og prøv igen.',
  500: 'Der opstod en serverfejl.',
  502: 'Serveren svarer ikke lige nu.',
  503: 'Tjenesten er midlertidigt utilgængelig - prøv igen om et øjeblik.',
  504: 'Forespørgslen tog for lang tid.',
};

/**
 * Zod's egne beskeder er engelske ("Required", "Invalid email"). Oversæt de
 * hyppigste, så en valideringsfejl ikke bliver halvt dansk, halvt engelsk.
 * Ukendte tilfælde falder tilbage på Zod's originaltekst frem for at skjule den.
 */
function translateIssue(d: ZodIssue): string {
  if (d.code === 'invalid_type' && d.received === 'undefined') return 'mangler';
  if (d.validation === 'email') return 'ugyldig e-mail';
  if (d.validation === 'url') return 'ugyldig URL';
  if (d.validation === 'uuid') return 'ugyldigt id';
  if (d.code === 'invalid_type') return `forventede ${d.expected ?? 'en anden type'}`;
  if (d.code === 'too_small') {
    return d.minimum === 1 ? 'må ikke være tom' : `skal være mindst ${d.minimum}`;
  }
  if (d.code === 'too_big') return `må højst være ${d.maximum}`;
  if (d.code === 'invalid_enum_value') return 'ikke en tilladt værdi';
  if (d.code === 'invalid_date') return 'ugyldig dato';
  return d.message ?? 'ugyldig værdi';
}

/** Gør Zod-valideringsfejl læselige: "kunde.email: ugyldig e-mail". */
function formatValidationDetails(details: ServerErrorBody['details']): string | null {
  if (!Array.isArray(details) || details.length === 0) return null;
  const parts = details
    .slice(0, 4)
    .map(d => {
      const field = Array.isArray(d.path) ? d.path.filter(p => p !== '').join('.') : '';
      const text = translateIssue(d);
      return field ? `${field}: ${text}` : text;
    })
    .filter((p): p is string => Boolean(p));
  if (parts.length === 0) return null;
  const more = details.length > parts.length ? ` (+${details.length - parts.length} flere)` : '';
  return parts.join(', ') + more;
}

export function getApiErrorMessage(error: unknown): string {
  const axiosError = error as AxiosError<ServerErrorBody> | undefined;

  if (axiosError?.isAxiosError) {
    const body = axiosError.response?.data;

    // Zod-valideringsfejl: "Validation error" alene siger intet, så vis felterne.
    if (body?.error === 'Validation error') {
      const detail = formatValidationDetails(body.details);
      if (detail) return `Ugyldige felter - ${detail}`;
    }

    // Serverens egen besked er altid at foretrække.
    if (typeof body?.error === 'string' && body.error.trim()) return body.error;
    if (typeof body?.message === 'string' && body.message.trim()) return body.message;

    // Intet svar overhovedet = netværks-/forbindelsesproblem, ikke en serverfejl.
    if (!axiosError.response) {
      return axiosError.code === 'ECONNABORTED'
        ? 'Forespørgslen tog for lang tid. Prøv igen.'
        : 'Kunne ikke få forbindelse til serveren. Tjek din internetforbindelse.';
    }

    const status = axiosError.response.status;
    return STATUS_FALLBACKS[status] ?? `Uventet fejl fra serveren (HTTP ${status}).`;
  }

  if (error instanceof Error && error.message) return error.message;
  if (typeof error === 'string' && error.trim()) return error;
  return 'Der opstod en uventet fejl.';
}
